import React from 'react';
import { FiServer, FiDollarSign, FiTrendingUp, FiCheckCircle } from 'react-icons/fi';
import { IconType } from 'react-icons';

export interface ComputeStats {
  total_instances: number;
  monthly_cost: number;
  potential_savings: number;
  optimizable_instances: number;
}

interface ComputeStatsCardsProps {
  stats: ComputeStats;
}

const ComputeStatsCards: React.FC<ComputeStatsCardsProps> = ({ stats }) => {
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount == null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num == null) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const cards: { title: string; value: string; icon: IconType; iconBg: string; iconColor: string; valueColor?: string }[] = [
    {
      title: 'Total Instances',
      value: formatNumber(stats.total_instances),
      icon: FiServer,
      iconBg: 'bg-blue-100 dark:bg-blue-900/50',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      title: 'Monthly Cost',
      value: formatCurrency(stats.monthly_cost),
      icon: FiDollarSign,
      iconBg: 'bg-orange-100 dark:bg-orange-900/50',
      iconColor: 'text-orange-600 dark:text-orange-400',
    },
    {
      title: 'Potential Savings',
      value: `${formatCurrency(stats.potential_savings)}/mo`,
      icon: FiTrendingUp,
      iconBg: 'bg-green-100 dark:bg-green-900/50',
      iconColor: 'text-green-600 dark:text-green-400',
      valueColor: 'text-green-600 dark:text-green-400',
    },
    {
      title: 'Optimizable Instances',
      value: formatNumber(stats.optimizable_instances),
      icon: FiCheckCircle,
      iconBg: 'bg-purple-100 dark:bg-purple-900/50',
      iconColor: 'text-purple-600 dark:text-purple-400',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.title}
            className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className={`flex-shrink-0 ${card.iconBg} rounded-lg p-3`}>
                <Icon className={`h-6 w-6 ${card.iconColor}`} />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {card.title}
                </p>
                <p
                  className={`text-2xl font-bold ${
                    card.valueColor || 'text-gray-900 dark:text-white'
                  }`}
                >
                  {card.value}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ComputeStatsCards;
